import { createContext, useCallback, useContext, useId, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import type { Tier, UpgradeFeature, UpgradeRequired } from '../api/types.ts';
import { t, type MessageKey } from '../i18n/index.ts';
import { Icon } from './Icon.tsx';
import { Modal } from './ui.tsx';

export const upgradePath = (feature?: UpgradeFeature | null) => (feature ? `/upgrade?feature=${encodeURIComponent(feature)}` : '/upgrade');

export const tierName = (tier: Tier) => t(`billing.tier.${tier}`);

/** The owner has to upgrade for an editor's seat; the editor can't do anything about it themselves. */
const ownerMustUpgrade = (feature: UpgradeFeature) => feature === 'editor_seat';

function body(upgrade: UpgradeRequired): string {
  const { feature, limit, tier } = upgrade;
  const vars = { tier: tierName(tier), limit: limit ?? '' };
  if (feature === 'editors') return t(!limit ? 'upgrade.body.editors' : limit === 1 ? 'upgrade.body.editors.one' : 'upgrade.body.editors.some', vars);
  if (limit === null && (feature === 'plans' || feature === 'shares' || feature === 'plan_people')) return t(`upgrade.body.${feature}.noNumber`, vars);
  return t(`upgrade.body.${feature}` as MessageKey, vars);
}

const ctaLabel = (feature: UpgradeFeature) => (feature === 'lapsed' ? t('billing.resubscribe') : t('upgrade.cta'));

export const upgradeTitle = (upgrade: Pick<UpgradeRequired, 'feature'>) => t(`upgrade.title.${upgrade.feature}`);

/** A calm card in place of an error: what limit was reached, and the way to more room. */
export function UpgradeNotice({ upgrade, onDismiss, className = '', hideCta = false }: { upgrade: UpgradeRequired; onDismiss?: () => void; className?: string; hideCta?: boolean }) {
  const id = useId();
  return (
    <section className={`upgrade-note ${className}`} aria-labelledby={id} role="status">
      <span className="upgrade-note__icon" aria-hidden="true"><Icon name="sparkle" size={17} /></span>
      <div className="upgrade-note__body">
        <h3 className="upgrade-note__title" id={id}>{upgradeTitle(upgrade)}</h3>
        <p className="upgrade-note__text">{body(upgrade)}</p>
        {!ownerMustUpgrade(upgrade.feature) && <p className="upgrade-note__fine">{t('upgrade.reassure')}</p>}
        <div className="upgrade-note__actions">
          {!ownerMustUpgrade(upgrade.feature) && !hideCta && (
            <Link to={upgradePath(upgrade.feature)} className="button button--sm button--outline-mint upgrade-note__cta">
              {ctaLabel(upgrade.feature)}<Icon name="arrowRight" size={14} />
            </Link>
          )}
          {onDismiss && <button type="button" className="text-button" onClick={onDismiss}>{t('upgrade.notNow')}</button>}
        </div>
      </div>
    </section>
  );
}

function UpgradeDialog({ upgrade, onClose }: { upgrade: UpgradeRequired; onClose: () => void }) {
  const bodyId = useId();
  const owner = ownerMustUpgrade(upgrade.feature);
  return (
    <Modal
      title={upgradeTitle(upgrade)}
      onClose={onClose}
      describedBy={bodyId}
      footer={
        owner ? (
          <button type="button" className="button button--primary" onClick={onClose}>{t('upgrade.ok')}</button>
        ) : (
          <>
            <button type="button" className="button button--ghost" onClick={onClose}>{t('upgrade.notNow')}</button>
            <Link to={upgradePath(upgrade.feature)} className="button button--primary" onClick={onClose}>{ctaLabel(upgrade.feature)}</Link>
          </>
        )
      }
    >
      <div className="upgrade-dialog" id={bodyId}>
        <span className="upgrade-note__icon" aria-hidden="true"><Icon name="sparkle" size={17} /></span>
        <div className="stack-xs">
          <p className="modal__text">{body(upgrade)}</p>
          {!owner && <p className="fine-print">{t('upgrade.reassure')}</p>}
        </div>
      </div>
    </Modal>
  );
}

const UpgradeContext = createContext<((upgrade: UpgradeRequired) => void) | null>(null);

/** One shared dialog for writes that fail with `upgrade_required` outside a form that can show it inline. */
export function UpgradeProvider({ children }: { children: ReactNode }) {
  const [upgrade, setUpgrade] = useState<UpgradeRequired | null>(null);
  const show = useCallback((next: UpgradeRequired) => setUpgrade(next), []);
  const value = useMemo(() => show, [show]);
  return (
    <UpgradeContext.Provider value={value}>
      {children}
      {upgrade && <UpgradeDialog upgrade={upgrade} onClose={() => setUpgrade(null)} />}
    </UpgradeContext.Provider>
  );
}

export function useUpgradePrompt() {
  const show = useContext(UpgradeContext);
  if (!show) throw new Error('useUpgradePrompt outside UpgradeProvider');
  return show;
}
